"use client"
import type React from "react"
import {
  type ComponentProps,
  type ElementRef,
  type MouseEventHandler,
  useMemo,
} from "react"
import { tv, type VariantProps } from "tailwind-variants"
import { classNames } from "../../utils"
import type { Block, BlockProps } from "../Block"
import { InlineFlex } from "../InlineFlex"
import { tableRowSizeVariants } from "./constants"
import { TableRowContext } from "./TableRowContext"
import { useTable } from "./useTable"

type Props = {
  className?: string
  children: React.ReactNode
  onClick?: MouseEventHandler<HTMLElement>
  ref?: React.Ref<ElementRef<typeof Block>>
} & Omit<BlockProps, "asChild"> &
  VariantProps<typeof tableRowVariants> &
  ComponentProps<"div">

const tableRowVariants = tv({
  variants: {
    interactive: {
      true: classNames(
        "group cursor-pointer hover:bg-bg-additional-1",
        "*:bg-transparent active:bg-bg-additional-2",
      ),
    },
    interactiveTransparent: {
      true: classNames(
        "group cursor-pointer hover:bg-bg-additional-1-transparent",
        "*:bg-transparent active:bg-bg-additional-2-transparent",
      ),
    },
  },
})

function TableRow({
  className,
  children,
  onClick,
  interactive: paramInteractive,
  interactiveTransparent,
  ...rest
}: Props) {
  const { size, dividers } = useTable()
  const interactive = paramInteractive ?? Boolean(onClick)
  const value = useMemo(() => ({ interactive }), [interactive])

  return (
    <TableRowContext.Provider value={value}>
      <InlineFlex
        className={classNames(
          "relative w-max min-w-full items-center gap-4",
          dividers ? "border-border-1 border-b" : "rounded",
          tableRowVariants({ interactive, interactiveTransparent }),
          tableRowSizeVariants({ size }).base(),
          className,
        )}
        onClick={onClick}
        role="row"
        {...rest}
      >
        {children}
      </InlineFlex>
    </TableRowContext.Provider>
  )
}

export { TableRow }
